import { useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { loadTokens, makeBuyOrder, makeSellOrder } from '../store/interactions'
import config from '../config.json'

import paxgIcon from '../assets/paxg.svg'
import usdtIcon from '../assets/usdt.svg'
import ethIcon from '../assets/eth.svg'
import mgIcon from '../assets/mg.svg'
import mgtvIcon from '../assets/mgtv.svg'

const tokenIcons = { PAXG: paxgIcon, USDT: usdtIcon, ETH: ethIcon, MG: mgIcon, MGTV: mgtvIcon }

// Priority: PAXG > USDT > ETH
const PRIORITY = { PAXG: 3, USDT: 2, ETH: 1 }

const BuySellModal = ({ isOpen, onClose, embedded }) => {
  const dispatch = useDispatch()
  const provider = useSelector(state => state.provider.connection)
  const chainId = useSelector(state => state.provider.chainId)
  const exchange = useSelector(state => state.exchange.contract)
  const tokens = useSelector(state => state.tokens.contracts)
  const decimals = useSelector(state => state.tokens.decimals)

  const [step, setStep] = useState(1)
  const [giveToken, setGiveToken] = useState('USDT')
  const [getToken, setGetToken] = useState('PAXG')
  const [giveAmount, setGiveAmount] = useState('')
  const [getAmount, setGetAmount] = useState('')

  if (!isOpen) return null

  const tokenNames = ['PAXG', 'USDT', 'ETH']
  const availableGetTokens = tokenNames.filter(t => t !== giveToken)
  const availableGiveTokens = tokenNames.filter(t => t !== getToken)

  const handleStep1Next = async () => {
    if (!chainId || !config[chainId]) return
    if (giveToken === getToken) return

    // Determine base/quote: higher priority is always token[0] (base)
    let baseSymbol, quoteSymbol
    if (PRIORITY[giveToken] > PRIORITY[getToken]) {
      baseSymbol = giveToken
      quoteSymbol = getToken
    } else {
      baseSymbol = getToken
      quoteSymbol = giveToken
    }

    const baseAddress = config[chainId][baseSymbol].address
    const quoteAddress = config[chainId][quoteSymbol].address
    await loadTokens(provider, [baseAddress, quoteAddress], dispatch)

    setStep(2)
  }

  const handlePlaceOrder = async () => {
    if (!giveAmount || !getAmount) return

    // Determine if this is a buy or sell
    // If giving the higher-priority token -> sell; getting higher-priority -> buy
    const isSell = PRIORITY[giveToken] > PRIORITY[getToken]

    // amount = base token amount (PAXG amount)
    // price = quote / base ratio
    let amount, price

    if (isSell) {
      // Giving PAXG (base), getting quote
      amount = giveAmount
      price = (parseFloat(getAmount) / parseFloat(giveAmount)).toString()
    } else {
      // Getting PAXG (base), giving quote
      amount = getAmount
      price = (parseFloat(giveAmount) / parseFloat(getAmount)).toString()
    }

    if (isSell) {
      await makeSellOrder(provider, exchange, tokens, { amount, price }, dispatch, decimals)
    } else {
      await makeBuyOrder(provider, exchange, tokens, { amount, price }, dispatch, decimals)
    }

    setStep(3)
  }

  const handleClose = () => {
    setStep(1)
    setGiveAmount('')
    setGetAmount('')
    onClose()
  }

  const priceRatio = giveAmount && getAmount && parseFloat(giveAmount) > 0
    ? (parseFloat(getAmount) / parseFloat(giveAmount)).toFixed(5)
    : null

  if (embedded) {
    return (
      <div className="buysell-embedded">
        <div className="buysell-modal__steps">
          <span className={step >= 1 ? 'buysell-modal__step--active' : ''}>1. Pair</span>
          <span className={step >= 2 ? 'buysell-modal__step--active' : ''}>2. Amount</span>
          <span className={step >= 3 ? 'buysell-modal__step--active' : ''}>3. Confirm</span>
        </div>

        {step === 1 && (
          <div className="buysell-modal__content">
            <h2>Create Order</h2>
            <div className="buysell-modal__pair">
              <div className="buysell-modal__token-select">
                <label>I want to give</label>
                <div className="buysell-modal__select-wrapper">
                  <img src={tokenIcons[giveToken]} alt={giveToken} />
                  <select value={giveToken} onChange={(e) => setGiveToken(e.target.value)}>
                    {availableGiveTokens.map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="buysell-modal__arrow">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M5 12H19M19 12L13 6M19 12L13 18" stroke="var(--clr-gold)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="buysell-modal__token-select">
                <label>I want to get</label>
                <div className="buysell-modal__select-wrapper">
                  <img src={tokenIcons[getToken]} alt={getToken} />
                  <select value={getToken} onChange={(e) => setGetToken(e.target.value)}>
                    {availableGetTokens.map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            <button className="button button--filled" onClick={handleStep1Next}>
              <span>Next</span>
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="buysell-modal__content">
            <h2>Set Amounts</h2>
            <p className="buysell-modal__pair-label">
              {giveToken} &rarr; {getToken}
            </p>
            <div className="buysell-modal__amounts">
              <div>
                <label>How much {giveToken} to give?</label>
                <input type="text" placeholder="0.0000" value={giveAmount} onChange={(e) => setGiveAmount(e.target.value)} />
              </div>
              <div>
                <label>How much {getToken} to get?</label>
                <input type="text" placeholder="0.0000" value={getAmount} onChange={(e) => setGetAmount(e.target.value)} />
              </div>
            </div>
            {priceRatio && (
              <p className="buysell-modal__ratio">1 {giveToken} = {priceRatio} {getToken}</p>
            )}
            <div className="buysell-modal__nav">
              <button className="button" onClick={() => setStep(1)}><span>Back</span></button>
              <button className="button button--filled" onClick={handlePlaceOrder}><span>Place Order</span></button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="buysell-modal__content buysell-modal__confirm">
            <div className="buysell-modal__check">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="var(--clr-green)" strokeWidth="1.5"/>
                <path d="M8 12L11 15L16 9" stroke="var(--clr-green)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h2>Order Created!</h2>
            <p>Your order has been placed successfully.</p>
            <button className="button button--filled" onClick={handleClose}><span>Done</span></button>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal buysell-modal" onClick={e => e.stopPropagation()}>
        <button className="modal__close" onClick={handleClose}>&times;</button>

        <div className="buysell-modal__steps">
          <span className={step >= 1 ? 'buysell-modal__step--active' : ''}>1. Pair</span>
          <span className={step >= 2 ? 'buysell-modal__step--active' : ''}>2. Amount</span>
          <span className={step >= 3 ? 'buysell-modal__step--active' : ''}>3. Confirm</span>
        </div>

        {step === 1 && (
          <div className="buysell-modal__content">
            <h2>Create Order</h2>
            <div className="buysell-modal__pair">
              <div className="buysell-modal__token-select">
                <label>I want to give</label>
                <div className="buysell-modal__select-wrapper">
                  <img src={tokenIcons[giveToken]} alt={giveToken} />
                  <select value={giveToken} onChange={(e) => setGiveToken(e.target.value)}>
                    {availableGiveTokens.map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="buysell-modal__arrow">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M5 12H19M19 12L13 6M19 12L13 18" stroke="var(--clr-gold)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>

              <div className="buysell-modal__token-select">
                <label>I want to get</label>
                <div className="buysell-modal__select-wrapper">
                  <img src={tokenIcons[getToken]} alt={getToken} />
                  <select value={getToken} onChange={(e) => setGetToken(e.target.value)}>
                    {availableGetTokens.map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <button className="button button--filled" onClick={handleStep1Next}>
              <span>Next</span>
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="buysell-modal__content">
            <h2>Set Amounts</h2>
            <p className="buysell-modal__pair-label">
              {giveToken} &rarr; {getToken}
            </p>

            <div className="buysell-modal__amounts">
              <div>
                <label>How much {giveToken} to give?</label>
                <input
                  type="text"
                  placeholder="0.0000"
                  value={giveAmount}
                  onChange={(e) => setGiveAmount(e.target.value)}
                />
              </div>
              <div>
                <label>How much {getToken} to get?</label>
                <input
                  type="text"
                  placeholder="0.0000"
                  value={getAmount}
                  onChange={(e) => setGetAmount(e.target.value)}
                />
              </div>
            </div>

            {priceRatio && (
              <p className="buysell-modal__ratio">
                1 {giveToken} = {priceRatio} {getToken}
              </p>
            )}

            <div className="buysell-modal__nav">
              <button className="button" onClick={() => setStep(1)}>
                <span>Back</span>
              </button>
              <button className="button button--filled" onClick={handlePlaceOrder}>
                <span>Place Order</span>
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="buysell-modal__content buysell-modal__confirm">
            <div className="buysell-modal__check">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="var(--clr-green)" strokeWidth="1.5"/>
                <path d="M8 12L11 15L16 9" stroke="var(--clr-green)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h2>Order Created!</h2>
            <p>You'll receive your funds when the order is filled. Go to <strong>Orders</strong> in the profile menu to view or cancel.</p>
            <button className="button button--filled" onClick={handleClose}>
              <span>Done</span>
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default BuySellModal
